import { DEFAULT_TERMINAL_ID } from "@cmmd-forge/contracts";

export interface TerminalControlTarget {
  readonly threadId: string;
  readonly terminalId: string;
  readonly sessionEpoch: string;
}

export interface TerminalControlSequenceIdentity {
  readonly sessionEpoch: string;
  readonly controllerId: string;
  readonly controlSequence: number;
}

interface TerminalControlState {
  readonly sessionEpoch: string;
  nextControlSequence: number;
  tail: Promise<void>;
  pendingWriteBytes: number;
}

interface TerminalControlSequencerOptions {
  readonly controllerId: string;
  readonly maxPendingWriteBytes: number;
}

interface EnqueueTerminalControlInput {
  readonly threadId: string;
  readonly terminalId?: string;
  readonly writeBytes?: number;
  readonly execute: (identity: TerminalControlSequenceIdentity) => Promise<unknown>;
}

function terminalKey(threadId: string, terminalId = DEFAULT_TERMINAL_ID): string {
  return `${threadId}\0${terminalId}`;
}

export function createTerminalControlSequencer(options: TerminalControlSequencerOptions) {
  const states = new Map<string, TerminalControlState>();
  let disposed = false;

  return {
    register(target: TerminalControlTarget): void {
      if (disposed) throw new Error("Terminal control sequencer is disposed.");
      const key = terminalKey(target.threadId, target.terminalId);
      const current = states.get(key);
      if (current?.sessionEpoch === target.sessionEpoch) return;
      states.set(key, {
        sessionEpoch: target.sessionEpoch,
        nextControlSequence: 1,
        tail: Promise.resolve(),
        pendingWriteBytes: 0,
      });
    },
    enqueue(input: EnqueueTerminalControlInput): Promise<void> {
      if (disposed) return Promise.reject(new Error("Terminal control sequencer is disposed."));
      const terminalId = input.terminalId ?? DEFAULT_TERMINAL_ID;
      const state = states.get(terminalKey(input.threadId, terminalId));
      if (!state)
        return Promise.reject(new Error("Terminal must be opened before sending controls."));
      const writeBytes = input.writeBytes ?? 0;
      if (state.pendingWriteBytes + writeBytes > options.maxPendingWriteBytes) {
        return Promise.reject(new Error("Terminal input queue is full while reconnecting."));
      }

      state.pendingWriteBytes += writeBytes;
      const operation = state.tail.then(async () => {
        const controlSequence = state.nextControlSequence;
        await input.execute({
          sessionEpoch: state.sessionEpoch,
          controllerId: options.controllerId,
          controlSequence,
        });
        state.nextControlSequence = controlSequence + 1;
      });
      state.tail = operation
        .finally(() => {
          state.pendingWriteBytes -= writeBytes;
        })
        .catch(() => undefined);
      return operation;
    },
    remove(threadId: string, terminalId?: string): void {
      if (terminalId) {
        states.delete(terminalKey(threadId, terminalId));
        return;
      }
      const prefix = `${threadId}\0`;
      for (const key of states.keys()) {
        if (key.startsWith(prefix)) states.delete(key);
      }
    },
    terminalIds(threadId: string): ReadonlyArray<string> {
      const prefix = `${threadId}\0`;
      return [...states.keys()]
        .filter((key) => key.startsWith(prefix))
        .map((key) => key.slice(prefix.length));
    },
    dispose(): void {
      disposed = true;
      states.clear();
    },
  };
}
